import { z } from 'zod';
import { dateToUTC } from 'shared/src/utils/zod/zod-utils';

// The Beeswax REST v2 API uses string enums and a nested budget object — NOT the v1 numeric
// budget_type / flat campaign_budget. Budget lives in `spend_budget`/`impressions_budget`
// { lifetime, daily } objects; the create body picks the one matching budget_type.
export const BEESWAX_BUDGET_TYPES = [
  'spend',
  'impressions',
  'spend including vendor fees'
] as const;
export const BEESWAX_LINE_ITEM_TYPES = ['banner', 'video', 'native'] as const;
export type BeeswaxBudgetType = (typeof BEESWAX_BUDGET_TYPES)[number];
export type BeeswaxLineItemType = (typeof BEESWAX_LINE_ITEM_TYPES)[number];
export type BeeswaxBudget = { lifetime?: number; daily?: number };

export type CreateCampaignOptions = {
  advertiser_id: number;
  name: string;
  budget_type: BeeswaxBudgetType;
  spend_budget?: BeeswaxBudget;
  impressions_budget?: BeeswaxBudget;
  start_date: string;
  end_date: string;
  active: boolean;
};

export type BiddingInput = {
  strategy: string;
  pacing?: 'lifetime' | 'daily' | 'none';
  values: { cpm_bid?: number };
};

export type BeeswaxPostBody = Record<string, string | boolean | number | object>;

export type CreateLineItemOptions = {
  campaign_id: number;
  // advertiser_id is read-only on v2 line items (inherited from the campaign), so it is NOT sent.
  name: string;
  type: BeeswaxLineItemType;
  budget_type: BeeswaxBudgetType;
  spend_budget?: BeeswaxBudget;
  impressions_budget?: BeeswaxBudget;
  bidding: BiddingInput;
  start_date: string;
  end_date: string;
  active: boolean;
};

const BUDGET_TYPE_MAP: Record<number, string> = {
  0: 'spend',
  1: 'impressions',
  2: 'spend including vendor fees'
};

const LINE_ITEM_TYPE_MAP: Record<number, string> = {
  0: 'banner',
  1: 'video',
  2: 'native'
};

// The AI-facing payloads carry human-readable strings ("spend", "banner"); the v2 wire also uses
// those strings directly, so create-facet passes them through. These maps normalize a legacy numeric
// value to the string form, then `.pipe(z.enum(...))` rejects any value outside the known set — so an
// unrecognized budget_type/type fails validation here instead of being cast straight onto the wire.
const BudgetTypeV2 = z
  .union([z.string(), z.number()])
  .transform(v => (typeof v === 'number' ? (BUDGET_TYPE_MAP[v] ?? String(v)) : v))
  .pipe(z.enum(BEESWAX_BUDGET_TYPES))
  .describe('"spend", "impressions", or "spend including vendor fees"');

const LineItemTypeV2 = z
  .union([z.string(), z.number()])
  .transform(v => (typeof v === 'number' ? (LINE_ITEM_TYPE_MAP[v] ?? String(v)) : v))
  .pipe(z.enum(BEESWAX_LINE_ITEM_TYPES))
  .describe('"banner", "video", or "native"');

const BiddingV2Schema = z.object({
  strategy: z.literal('CPM').describe('Bidding strategy, e.g. "CPM"'),
  cpm_bid: z.number().optional().describe('CPM bid amount in dollars'),
  pacing: z
    .union([z.literal('lifetime'), z.literal('daily'), z.literal('flight'), z.literal('none')])
    .nullable()
    .optional()
    .describe('Pacing strategy')
});

const SpendBudgetSchema = z.object({
  lifetime: z.string().describe('Lifetime budget as a decimal string, e.g. "700.35"'),
  daily: z.string().nullable().optional().describe('Daily budget cap as a decimal string')
});

export const CreateCampaignPayload = z.object({
  name: z.string().describe('Name of the campaign'),
  budget_type: BudgetTypeV2.describe('"spend", "impressions", or "spend including vendor fees"'),
  spend_budget: SpendBudgetSchema.optional().describe(
    'Budget when budget_type is "spend" or "spend including vendor fees"'
  ),
  start_date: z.string().describe('Start date in YYYY-MM-DD HH:mm:ss format'),
  end_date: z.string().describe('End date in YYYY-MM-DD HH:mm:ss format')
});
export type CreateCampaignPayload = z.infer<typeof CreateCampaignPayload>;

export const CreateLineItemPayload = z.object({
  campaign_id: z.number().describe('ID of the parent campaign'),
  type: LineItemTypeV2.describe('"banner", "video", or "native"'),
  name: z.string().describe('Name of the line item'),
  budget_type: BudgetTypeV2.describe('"spend", "impressions", or "spend including vendor fees"'),
  spend_budget: SpendBudgetSchema.optional().describe(
    'Budget when budget_type is "spend" or "spend including vendor fees"'
  ),
  bidding: BiddingV2Schema.describe('Bidding configuration'),
  start_date: z.string().describe('Start date in YYYY-MM-DD HH:mm:ss format'),
  end_date: z.string().describe('End date in YYYY-MM-DD HH:mm:ss format')
});
export type CreateLineItemPayload = z.infer<typeof CreateLineItemPayload>;

export const BeeswaxCreateResponse = z
  .object({
    id: z.number()
  })
  .passthrough();

export type BeeswaxCreateResponse = z.infer<typeof BeeswaxCreateResponse>;

export const BeeswaxPaginatedResponse = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(z.object({}).catchall(z.any()))
});
export type BeeswaxPaginatedResponse = z.infer<typeof BeeswaxPaginatedResponse>;

// v2 read shape: { strategy, values: { cpm_bid }, pacing, ... }. Loose so the extra v2 fields
// (pacing_behavior, multiplier, bid_shading_control, …) and a null bidding pass through.
const BiddingObject = z.looseObject({
  strategy: z.string().optional(),
  values: z
    .object({
      cpm_bid: z.number().optional(),
      cpa_target: z.number().optional()
    })
    .nullable()
    .optional(),
  pacing: z.string().nullable().optional()
});

// v2 budget object: { lifetime, daily }. Beeswax returns spend amounts as decimal STRINGS
// (e.g. "10.00") and impression counts as numbers, so accept either.
const BeeswaxMoney = z.union([z.number(), z.string()]).nullable().optional();
const BeeswaxBudgetObject = z.object({
  lifetime: BeeswaxMoney,
  daily: BeeswaxMoney
});

// Lenient on purpose: fetchBeeswaxAdvertiser is fetched BY id (so the caller already knows the id),
// and v2 GETs (single-resource AND list rows) return the advertiser's own key as `id` rather than
// `advertiser_id` (mirroring campaigns/line-items). The v1/Buzz names are kept as fallbacks; every
// field is optional and the successful fetch itself is the existence/ownership check.
export const BeeswaxAdvertiserV1 = z.looseObject({
  id: z.number().optional(),
  advertiser_id: z.number().optional(),
  name: z.string().optional(),
  advertiser_name: z.string().optional()
});
export type BeeswaxAdvertiserV1 = z.infer<typeof BeeswaxAdvertiserV1>;

// These schemas describe the Beeswax REST v2 *live API* read shape and are applied only to live
// fetch responses (fetchBeeswaxCampaign/-LineItem). They are NOT the shape of stored
// media_platform_entities.raw_entity: the periodic sync writes a separate wrapped
// `{ data: { …legacy v1/Buzz fields: line_item_name, numeric budget_type, bidding.bidding_strategy… },
// schema: "brkthru.com/beeswax_line_item/1-0-0" }` envelope (see
// db/migrations/V20250128164636__fix-beeswax-raw-entity.sql). Nothing parses stored raw_entity with
// these schemas, so the two shapes coexist; don't reuse these to validate DB rows.
//
// Beeswax REST v2 campaign read shape. looseObject keeps the many extra v2 fields (currency,
// continents, revenue, account_id, …) we don't model.
export const BeeswaxCampaign = z.looseObject({
  id: z.number(),
  advertiser_id: z.number(),
  name: z.string(),
  active: z.boolean(),
  budget_type: z.string().nullable().optional(),
  spend_budget: BeeswaxBudgetObject.nullable().optional(),
  impressions_budget: BeeswaxBudgetObject.nullable().optional(),
  start_date: dateToUTC,
  end_date: dateToUTC.nullable().optional()
});

export type BeeswaxCampaign = z.infer<typeof BeeswaxCampaign>;

// Beeswax REST v2 line-item read shape (own key is `id`; `type` is a string; budget is nested).
export const BeeswaxLineItem = z.looseObject({
  id: z.number(),
  advertiser_id: z.number(),
  campaign_id: z.number(),
  name: z.string(),
  active: z.boolean(),
  type: z.string(),
  budget_type: z.string().nullable().optional(),
  spend_budget: BeeswaxBudgetObject.nullable().optional(),
  impressions_budget: BeeswaxBudgetObject.nullable().optional(),
  bidding: BiddingObject.nullable().optional(),
  start_date: dateToUTC,
  end_date: dateToUTC.nullable().optional(),
  account_id: z.number().optional(),
  targeting_expression_id: z.number().nullable().optional()
});
export type BeeswaxLineItem = z.infer<typeof BeeswaxLineItem>;
